import { createContext, useContext, useLayoutEffect } from 'react'

/**
 * What the bar across the top is called right now.
 *
 * "Hazel's Games" everywhere, except while a game is on screen — then it is
 * that game's name. Two names for the same thing was one name too many: she
 * knows what she is playing by the time the board is up, and the heading
 * saying so again cost 120 measured pixels of a 1080px television, on the one
 * display with the least room to give.
 *
 * A title *string*, not a slot to portal into, which is where this differs
 * from `legend-slot.ts` next door. The legend is additive — an empty slot
 * costs nothing, so a portal fits it. A title is a *replacement*: the bar says
 * the game's name or the app's and never both, and a portal cannot express
 * "and hide what was there". Two portals into one node would also append
 * rather than replace, which is a bar reading "Hazel's GamesStripey's Sudoku"
 * the first time two screens overlap.
 */
export interface BarTitle {
  /** The game on screen, or null for the app's own name. */
  readonly title: string | null
  readonly show: (title: string | null) => void
}

export const BarTitleContext = createContext<BarTitle>({
  title: null,
  show: () => undefined,
})

/**
 * Names the bar for as long as this screen is on it.
 *
 * Called once per screen at the top of the component, rather than rendered as
 * an element where the heading used to be — the games that ask how big first
 * drew their title on the chooser *and* on the board, and one call at the top
 * covers both without the name blinking out in between.
 *
 * `useLayoutEffect`, and that is not a preference. An effect that runs after
 * paint shows one frame of "Hazel's Games" before the game's name, which on a
 * television is a visible flicker at the start of every round.
 */
export function useBarTitle(title: string | null): void {
  const { show } = useContext(BarTitleContext)

  useLayoutEffect(() => {
    show(title)

    // Cleared on the way out, so leaving a game hands the bar back rather than
    // leaving the last game's name over the picker.
    return () => show(null)
  }, [show, title])
}

/** What the bar should draw. Read by the header, not by a screen. */
export function useBarTitleShown(): string | null {
  return useContext(BarTitleContext).title
}
